import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { notificationsService, type NotificationItem } from '../services/notifications';
import { ApiError } from '../services/api';
import { notificationSocket } from '../lib/notificationSocket';
import { useAuth } from './useAuth';

const POLL_INTERVAL_MS = 30_000;

/**
 * Contador de não lidas (polling, mesmo padrão de useAttendanceUnreadCount)
 * + carregamento sob demanda da lista + navegação até a entidade (card),
 * compartilhado entre o dropdown do sino (TopBar) e a página completa
 * (/notificacoes).
 */
export function useNotifications() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [unreadCount, setUnreadCount] = useState(0);
  const [items, setItems] = useState<NotificationItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [resolvingId, setResolvingId] = useState<string | null>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    cancelledRef.current = false;

    const fetchCount = async () => {
      try {
        const { count } = await notificationsService.getUnreadCount();
        if (!cancelledRef.current) setUnreadCount(count);
      } catch {
        // silencioso — badge não é crítico
      }
    };

    fetchCount();
    // Aba em segundo plano não consulta (o WS de notificações continua chegando).
    const interval = setInterval(() => { if (!document.hidden) fetchCount(); }, POLL_INTERVAL_MS);
    return () => {
      cancelledRef.current = true;
      clearInterval(interval);
    };
  }, [user]);

  // Bump em tempo real via WS (ver notificationSocket.ts) — a conexão em si
  // é gerenciada uma única vez por AppLayout.tsx (initNotificationRealtime);
  // este hook só se inscreve para refletir o evento no seu próprio estado
  // local. Seguro chamar de múltiplas instâncias simultâneas (TopBar +
  // /notificacoes) — cada uma registra seu próprio handler independente,
  // sem abrir conexão nova.
  useEffect(() => {
    if (!user) return;

    const onNew = (n: NotificationItem) => {
      setUnreadCount((prev) => prev + 1);
      setItems((prev) => (prev.some((x) => x.id === n.id) ? prev : [n, ...prev]));
    };

    notificationSocket.on('notification.new', onNew);
    return () => notificationSocket.off('notification.new', onNew);
  }, [user]);

  const fetchList = useCallback(async () => {
    setLoading(true);
    try {
      const res = await notificationsService.list();
      setItems(res.results);
    } catch {
      // silencioso — dropdown cai no empty state
    } finally {
      setLoading(false);
    }
  }, []);

  const markRead = useCallback(async (id: string) => {
    setItems((prev) => prev.map((n) => (n.id === id ? { ...n, is_read: true } : n)));
    setUnreadCount((prev) => Math.max(0, prev - 1));
    try {
      await notificationsService.markRead(id);
    } catch {
      // otimista — próximo poll/abertura do dropdown corrige se falhar
    }
  }, []);

  const markAllRead = useCallback(async () => {
    setItems((prev) => prev.map((n) => ({ ...n, is_read: true })));
    setUnreadCount(0);
    try {
      await notificationsService.markAllRead();
    } catch {
      // otimista — próximo poll/abertura do dropdown corrige se falhar
    }
  }, []);

  /**
   * Marca como lida e, se a notificação referencia um card, resolve o
   * board (mirror-aware, ver notificationsService.resolveCard) e navega
   * até ele com o card já selecionado (?card=id, lido por BoardCompleto).
   */
  const openNotification = useCallback(
    async (n: NotificationItem) => {
      if (!n.is_read) markRead(n.id);

      // Atendimento (precisa de humano / encaminhado) — mesmo destino do
      // toast em AppLayout.tsx.
      if (n.entity_type === 'session' && n.entity_id) {
        navigate(`/atendimento/conversas?focusSessionId=${n.entity_id}`);
        return;
      }
      if (n.entity_type !== 'card' || !n.entity_id) return;

      setResolvingId(n.id);
      try {
        const { board } = await notificationsService.resolveCard(n.entity_id);
        navigate(`/board/${board}?card=${n.entity_id}`);
      } catch (err) {
        if (err instanceof ApiError && err.status === 403) {
          toast.error('Você não tem mais acesso a este card.');
        } else if (err instanceof ApiError && err.status === 404) {
          toast.error('Este card não está mais disponível.');
        } else {
          toast.error('Não foi possível abrir o card.');
        }
      } finally {
        setResolvingId(null);
      }
    },
    [markRead, navigate],
  );

  return { unreadCount, items, loading, resolvingId, fetchList, markRead, markAllRead, openNotification };
}
